import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  TextField,
  Tooltip,
  Chip,
} from "../../Collections/Imports";
import { Alert, ToggleButton, ToggleButtonGroup } from "@mui/material";
import CustomButton from "../../Components/CommonComponents/GlobalButton";
import { useApiCallMutation } from "../../Services/ApiCallService";
import { campaignModuleAPiEndPoints } from "../../Config/APIsEndpoints";
import {
  apiErrorSection,
  buildIntegrationPayload,
  classifyApiResult,
  listSecretRefs,
  maskSecretsForDisplay,
  parseJsonText,
  readAccessToken,
  SECRET_HOST_HINT,
  SECRET_RULE_HINT,
  TEST_LEAD_TOOLTIPS,
  toEditorText,
} from "./integrationLogic";
import { BUYER_INTEGRATION_SAMPLES, getSample } from "./samples";
import { probeSendTestLead } from "./testLeadProbe";

const EMPTY_ERRORS = {
  requestTemplate: [],
  responseRules: [],
  timeouts: [],
  general: [],
};

const editorSx = {
  width: "100%",
  "& textarea": {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: "13px",
    lineHeight: 1.5,
    whiteSpace: "pre",
    overflowX: "auto",
  },
  "& .MuiInputBase-root": { background: "#fafbfd" },
};

const textsFrom = (data) => ({
  requestTemplate: toEditorText(data?.requestTemplate),
  responseRules: toEditorText(data?.responseRules),
  timeouts: toEditorText({
    timeout: data?.timeout,
    ...(data?.minTimeoutMs !== undefined && data?.minTimeoutMs !== null
      ? { minTimeoutMs: data.minTimeoutMs }
      : {}),
  }),
});

function JsonEditor({ name, label, help, value, onChange, errors, disabled, rows }) {
  const reformat = () => {
    const parsed = parseJsonText(value);
    if (!parsed.error && !parsed.empty) onChange(toEditorText(parsed.value));
  };
  return (
    <Box sx={{ mt: "18px" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography component="p" fontWeight={600}>
          {label}
        </Typography>
        <Button size="small" onClick={reformat} disabled={disabled}>
          Format JSON
        </Button>
      </Box>
      <Typography component="p" variant="caption" color="text.secondary" mb={1}>
        {help}
      </Typography>
      <TextField
        multiline
        minRows={rows || 8}
        maxRows={30}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        error={errors.length > 0}
        sx={editorSx}
        inputProps={{
          spellCheck: false,
          "aria-label": label,
          "data-testid": `editor-${name}`,
        }}
      />
      {errors.map((msg, i) => (
        <Typography
          key={i}
          component="p"
          variant="caption"
          color="error"
          data-testid={`error-${name}`}
        >
          {msg}
        </Typography>
      ))}
    </Box>
  );
}

// The API's answer when the campaign has no integration of the requested kind (HTTP 200, envelope 400).
function isMissingIntegration(outcome) {
  return (
    outcome.kind === "error" &&
    outcome.httpStatus === null &&
    /no campaign found/i.test(outcome.message || "")
  );
}

// RTK Query triggers can reject (e.g. a throwing baseQuery); never let that leave the screen half-open.
async function callApi(trigger, args) {
  try {
    return await trigger(args);
  } catch (e) {
    return { error: { status: "FETCH_ERROR" } };
  }
}

function BuyerIntegrationForm() {
  const [GetCampaignIntegration] = useApiCallMutation();
  const [AddIntegrationSetting] = useApiCallMutation();
  const [SendTestLead] = useApiCallMutation();

  const currentCampaignData = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("currentCampaignData")) || {};
    } catch (e) {
      return {};
    }
  }, []);
  const campaignId = currentCampaignData?.campaignId;
  // staff login (lds-api #340): every /buyer/** route needs the LDS admin role; the API's 401/403 closes the screen

  const [base, setBase] = useState(null);
  const [texts, setTexts] = useState({
    requestTemplate: "",
    responseRules: "",
    timeouts: "",
  });
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadCount, setLoadCount] = useState(0);
  const [testLead, setTestLead] = useState({ enabled: false, reason: "checking" });
  const [testResult, setTestResult] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  // live and sandbox are separate integrations with their own template and rules (lds-api #333 stack)
  const [mode, setMode] = useState("live");
  const isSandbox = mode === "sandbox";
  // only the latest load may update the screen (a late sandbox answer must never land in the live editor)
  const loadGeneration = useRef(0);

  const applyServerData = useCallback(
    (data) => {
      setBase({
        campaignId,
        campaignIntegration: data?.campaignIntegration,
        postingUrl: data?.postingUrl,
        customField: data?.customField || [],
      });
      setTexts(textsFrom(data));
      setErrors(EMPTY_ERRORS);
    },
    [campaignId]
  );

  const fetchIntegration = useCallback(
    (sandbox) =>
      callApi(GetCampaignIntegration, {
        endPoint: campaignModuleAPiEndPoints.getCampaignIntegration,
        method: "POST",
        data: { data: { campaignId, sandbox } },
      }),
    [GetCampaignIntegration, campaignId]
  );

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    const stale = () => generation !== loadGeneration.current;
    setLoading(true);
    setNotice(null);
    setLoaded(false);
    setBase(null);
    const outcome = classifyApiResult(await fetchIntegration(isSandbox));
    if (stale()) return;
    if (outcome.kind === "ok") {
      applyServerData(outcome.data);
      setLoaded(true);
      setLoadCount((n) => n + 1);
    } else if (outcome.kind === "forbidden") {
      setForbidden(true);
    } else if (outcome.kind === "error" && !isMissingIntegration(outcome)) {
      setBase({ campaignId });
      setErrors({
        ...EMPTY_ERRORS,
        general: [
          `Could not load the ${isSandbox ? "sandbox" : "live"} integration${
            outcome.message ? `: ${maskSecretsForDisplay(outcome.message)}` : ""
          }. Try Reload saved.`,
        ],
      });
    } else if (isSandbox) {
      // no sandbox integration yet: start one from the live basics, with its own posting URL
      const live = classifyApiResult(await fetchIntegration(false));
      if (stale()) return;
      if (live.kind === "forbidden") {
        setForbidden(true);
      } else if (live.kind === "ok") {
        setBase({
          campaignId,
          campaignIntegration: live.data?.campaignIntegration,
          postingUrl: "",
          customField: live.data?.customField || [],
        });
        setTexts(textsFrom({ timeout: live.data?.timeout }));
        setErrors(EMPTY_ERRORS);
        setNotice({
          color: "info",
          text: "This campaign has no sandbox integration yet. Enter the buyer's sandbox posting URL, set the template and rules (or load a sample), then Save to create it.",
        });
        setLoaded(true);
        setLoadCount((n) => n + 1);
      } else {
        setBase({ campaignId });
        setErrors({
          ...EMPTY_ERRORS,
          general: [
            "Set up the live integration on the Integration tab first; the sandbox starts from its settings.",
          ],
        });
      }
    } else {
      setBase({ campaignId });
      setErrors({
        ...EMPTY_ERRORS,
        general: [
          "This campaign has no integration yet. Set the integration type, posting URL and custom fields on the Integration tab first.",
        ],
      });
    }
    setLoading(false);
  }, [fetchIntegration, applyServerData, campaignId, isSandbox]);

  useEffect(() => {
    if (campaignId) load();
    else setLoading(false);
  }, [campaignId, load]);

  useEffect(() => {
    if (!loaded) return undefined;
    let active = true;
    setTestLead({ enabled: false, reason: "checking" });
    // read the token now: the load may have refreshed it
    probeSendTestLead(readAccessToken()).then((state) => {
      if (!active) return;
      if (state.reason === "forbidden") setForbidden(true);
      setTestLead(state);
    });
    return () => {
      active = false;
    };
  }, [loaded, loadCount]);

  const onSendTestLead = async () => {
    setSendingTest(true);
    setTestResult(null);
    const result = await callApi(SendTestLead, {
      endPoint: campaignModuleAPiEndPoints.sendTestLead,
      method: "POST",
      data: { data: { campaignId } },
    });
    setSendingTest(false);
    const outcome = classifyApiResult(result);
    if (outcome.kind === "ok") {
      setTestResult({ ok: true, data: maskSecretsForDisplay(outcome.data) });
    } else if (outcome.kind === "forbidden") {
      setForbidden(true);
    } else {
      setTestResult({
        ok: false,
        message: maskSecretsForDisplay(outcome.message || "The test lead was not sent."),
      });
    }
  };

  const setText = (name) => (value) => {
    setTexts((prev) => ({ ...prev, [name]: value }));
    setNotice(null);
  };

  const loadSample = (key) => {
    const sample = getSample(key);
    if (!sample) return;
    setTexts({
      requestTemplate: toEditorText(sample.requestTemplate),
      responseRules: toEditorText(sample.responseRules),
      timeouts: toEditorText(sample.timeouts),
    });
    setErrors(EMPTY_ERRORS);
    setNotice({
      color: "info",
      text: `Loaded "${sample.label}". Replace every PLACEHOLDER value and each LDS_BUYER_SECRET_* name with this buyer's, then Save.`,
    });
  };

  const onSave = async () => {
    const { errors: found, payload } = buildIntegrationPayload(base, texts, undefined, {
      sandbox: isSandbox,
    });
    setErrors(found);
    setNotice(null);
    if (!payload) return;
    setSaving(true);
    const result = await callApi(AddIntegrationSetting, {
      endPoint: campaignModuleAPiEndPoints.createCampaignIntegration,
      method: "POST",
      data: { data: payload },
    });
    setSaving(false);
    const outcome = classifyApiResult(result);
    if (outcome.kind === "ok") {
      applyServerData(outcome.data);
      setNotice({
        color: "success",
        text: isSandbox ? "Sandbox integration saved." : "Live integration saved.",
      });
    } else if (outcome.kind === "forbidden") {
      setForbidden(true);
    } else {
      const message =
        outcome.message || "Save failed: the API did not accept the integration.";
      setErrors({ ...EMPTY_ERRORS, [apiErrorSection(message)]: [message] });
    }
  };

  const parsedTemplate = parseJsonText(texts.requestTemplate);
  const secretRefs = parsedTemplate.error ? [] : listSecretRefs(parsedTemplate.value);
  const editable = loaded && !forbidden && !loading && !saving;
  const testTooltip = testLead.enabled
    ? TEST_LEAD_TOOLTIPS.ready
    : TEST_LEAD_TOOLTIPS[testLead.reason] || TEST_LEAD_TOOLTIPS.missing;

  if (!campaignId) {
    return (
      <Alert severity="warning">
        Open a campaign from the Campaigns list to configure its buyer integration.
      </Alert>
    );
  }

  if (forbidden) {
    return (
      <Alert severity="warning" data-testid="admin-required">
        Buyer integrations need the LDS admin role (staff login), or your login has expired. Sign in
        again, or ask an LDS admin for access.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: "80%", margin: "0 auto" }} data-testid="buyer-integration">
      <Box marginBottom={"15px"}>
        <Typography component="h6" variant="h6" fontWeight={600}>
          Buyer Integration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          How LDS posts leads for {currentCampaignData?.campaignName || "this campaign"} to the
          buyer, how it reads the answer and how long it waits. Live and sandbox are separate
          integrations: live gets real leads, sandbox gets only test leads.
        </Typography>
        <Box display="flex" alignItems="center" gap={2} mt={1.5} flexWrap="wrap">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(e, value) => {
              if (value && value !== mode && !saving && !loading) {
                setMode(value);
                setTestResult(null);
              }
            }}
            aria-label="Integration to edit"
            disabled={saving}
          >
            <ToggleButton value="live" data-testid="mode-live">
              Live
            </ToggleButton>
            <ToggleButton value="sandbox" data-testid="mode-sandbox">
              Sandbox
            </ToggleButton>
          </ToggleButtonGroup>
          {isSandbox ? (
            <TextField
              size="small"
              label="Sandbox posting URL"
              value={base?.postingUrl || ""}
              onChange={(e) => {
                const postingUrl = e.target.value;
                setBase((prev) => ({ ...(prev || { campaignId }), postingUrl }));
                setNotice(null);
              }}
              disabled={!editable}
              sx={{ minWidth: "420px", flex: 1 }}
              inputProps={{ "data-testid": "posting-url", spellCheck: false }}
            />
          ) : (
            <Typography variant="body2">
              Posting URL: <b>{base?.postingUrl || "not set"}</b> (edit on the Integration tab)
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: "12px" }} />

      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
        <Typography variant="body2" fontWeight={600} mr={1}>
          Load sample:
        </Typography>
        {BUYER_INTEGRATION_SAMPLES.map((s) => (
          <Tooltip key={s.key} title={s.description}>
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!editable}
                onClick={() => loadSample(s.key)}
              >
                {s.label}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Box>

      {notice && (
        <Alert severity={notice.color} sx={{ mt: 2 }}>
          {notice.text}
        </Alert>
      )}
      {errors.general.map((msg, i) => (
        <Alert key={i} severity="error" sx={{ mt: 2 }} data-testid="error-general">
          {msg}
        </Alert>
      ))}

      <Grid container columnSpacing={3}>
        <Grid item xs={12} md={7}>
          <JsonEditor
            name="requestTemplate"
            label="Request template"
            help="Format (form, json, xml), fields (buyerField from ldsField, value, secretRef or envRef (LDS_BUYER_VALUE_* for non-secret configuration); transforms, valueMap, defaultValue, required, omitIfEmpty) and auth. Empty = the legacy form post."
            value={texts.requestTemplate}
            onChange={setText("requestTemplate")}
            errors={errors.requestTemplate}
            disabled={!editable}
            rows={20}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <JsonEditor
            name="responseRules"
            label="Response rules"
            help="statusPath, outcomes (value to sold, rejected, in_progress, error, not_found), optional pricePath, redirectUrlPath, reasonPath. Empty = the legacy JSON status 1-5."
            value={texts.responseRules}
            onChange={setText("responseRules")}
            errors={errors.responseRules}
            disabled={!editable}
            rows={10}
          />
          <JsonEditor
            name="timeouts"
            label="Timeouts (milliseconds)"
            help="timeout: 100-60000 ms for the whole buyer call. minTimeoutMs (optional): least time the buyer needs; at least 1,000 ms below timeout. Every lead has 30 s in total."
            value={texts.timeouts}
            onChange={setText("timeouts")}
            errors={errors.timeouts}
            disabled={!editable}
            rows={4}
          />
          <Box sx={{ mt: "18px" }} data-testid="secret-names">
            <Typography component="p" fontWeight={600}>
              Secrets used (env var names)
            </Typography>
            <Typography component="p" variant="caption" color="text.secondary" mb={1}>
              {SECRET_RULE_HINT}
            </Typography>
            <Typography
              component="p"
              variant="caption"
              color="text.secondary"
              mb={1}
              data-testid="secret-host-hint"
            >
              {SECRET_HOST_HINT}
            </Typography>
            {secretRefs.length === 0 ? (
              <Typography variant="body2">None</Typography>
            ) : (
              secretRefs.map((ref) => (
                <Box key={`${ref.where}-${ref.name}`} mb={0.5}>
                  <Chip size="small" label={ref.name} sx={{ fontFamily: "monospace", mr: 1 }} />
                  <Typography variant="caption">{ref.where}</Typography>
                </Box>
              ))
            )}
          </Box>
        </Grid>
      </Grid>

      <Box display="flex" gap={2} pt={3} pb={3}>
        <CustomButton
          color="#3a5396"
          label={saving ? "Saving..." : "Save"}
          onClick={onSave}
          disabled={!editable || !base?.postingUrl}
        />
        <Button variant="outlined" onClick={load} disabled={loading || saving}>
          Reload saved
        </Button>
        <Tooltip title={testTooltip}>
          <span>
            <Button
              variant="outlined"
              disabled={!testLead.enabled || !editable || sendingTest}
              onClick={onSendTestLead}
              data-testid="send-test-lead"
            >
              {sendingTest ? "Sending..." : "Send test lead"}
            </Button>
          </span>
        </Tooltip>
      </Box>
      {testResult && (
        <Alert
          severity={testResult.ok ? "success" : "error"}
          sx={{ mb: 3 }}
          data-testid="test-lead-result"
        >
          {testResult.ok ? (
            <>
              Test lead sent to the sandbox integration. API answer:
              <Box
                component="pre"
                sx={{ fontFamily: "monospace", fontSize: "12px", whiteSpace: "pre-wrap", m: 0 }}
              >
                {JSON.stringify(testResult.data, null, 2)}
              </Box>
            </>
          ) : (
            testResult.message
          )}
        </Alert>
      )}
    </Box>
  );
}

export default BuyerIntegrationForm;
